import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import axios from "axios";
import { 
  CreditCard, 
  Briefcase, 
  Users, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  Home,
  Building2,
  Wallet,
  ShoppingCart,
  Car,
  GraduationCap,
  Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Debt Level", icon: CreditCard },
  { id: 2, title: "Employment", icon: Briefcase },
  { id: 3, title: "Creditors", icon: Users }
];

const EMPLOYMENT_OPTIONS = [
  { value: "employed_full", label: "Employed Full-Time", icon: Briefcase },
  { value: "employed_part", label: "Employed Part-Time", icon: Briefcase },
  { value: "self_employed", label: "Self-Employed", icon: Building2 },
  { value: "unemployed", label: "Unemployed", icon: Users },
  { value: "retired", label: "Retired", icon: Home },
  { value: "benefits", label: "On Benefits", icon: Wallet }
];

const RESIDENTIAL_OPTIONS = [
  { value: "homeowner", label: "Homeowner", icon: Home },
  { value: "tenant", label: "Renting", icon: Building2 },
  { value: "living_family", label: "Living with Family", icon: Users }
];

const DEBT_TYPES = [
  { value: "credit_cards", label: "Credit Cards", icon: CreditCard },
  { value: "personal_loans", label: "Personal Loans", icon: Wallet },
  { value: "overdrafts", label: "Overdrafts", icon: Building2 },
  { value: "store_cards", label: "Store Cards", icon: ShoppingCart },
  { value: "car_finance", label: "Car Finance", icon: Car },
  { value: "student_loans", label: "Student Loans", icon: GraduationCap },
  { value: "payday_loans", label: "Payday Loans", icon: Phone },
  { value: "catalogues", label: "Catalogues", icon: ShoppingCart }
];

const StartCasePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    debt_amount: 15000,
    employment_status: "",
    residential_status: "",
    monthly_income: 2000,
    number_of_creditors: 3,
    debt_types: []
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.debt_amount >= 5000;
      case 2:
        if (!formData.employment_status) {
          toast.error("Please select your employment status");
          return false;
        }
        if (!formData.residential_status) {
          toast.error("Please select your residential status");
          return false;
        }
        return true;
      case 3:
        if (formData.number_of_creditors < 2) {
          toast.error("IVA requires at least 2 creditors");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/cases`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Application submitted successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDebtType = (value) => {
    setFormData(prev => ({
      ...prev,
      debt_types: prev.debt_types.includes(value)
        ? prev.debt_types.filter(t => t !== value)
        : [...prev.debt_types, value]
    }));
  };

  const progressPercent = (currentStep / 3) * 100;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="start-case-page">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-4">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isActive 
                        ? 'bg-[#10B981] text-white shadow-lg shadow-emerald-200' 
                        : isCompleted 
                          ? 'bg-[#10B981] text-white' 
                          : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-6 h-6" />
                    ) : (
                      <Icon className="w-6 h-6" />
                    )}
                  </div>
                  <span className={`text-sm mt-2 font-medium ${
                    isActive ? 'text-[#10B981]' : 'text-slate-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Form Steps */}
        <AnimatePresence mode="wait">
          {/* Step 1: Debt Level */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl p-8 border border-slate-200"
            >
              <h2 className="text-2xl font-bold text-[#0F172A] mb-2">How much debt do you have?</h2>
              <p className="text-slate-600 mb-8">Include all unsecured debts</p>

              <div className="bg-slate-50 rounded-2xl p-8">
                <div className="text-center mb-6">
                  <span className="font-numbers text-5xl font-bold text-[#0F172A]">
                    {formatCurrency(formData.debt_amount)}
                  </span>
                </div>
                <Slider
                  value={[formData.debt_amount]}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, debt_amount: value[0] }))}
                  min={5000}
                  max={100000}
                  step={500}
                  className="w-full"
                />
                <div className="flex justify-between mt-3 text-sm text-slate-400">
                  <span>£5,000</span>
                  <span>£100,000+</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Employment */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Your employment status</h2>
                <p className="text-slate-600">This helps us assess your eligibility</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#0F172A] mb-4">Employment Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {EMPLOYMENT_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.employment_status === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setFormData(prev => ({ ...prev, employment_status: option.value }))}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-[#10B981] bg-emerald-50' 
                            : 'border-slate-200 hover:border-[#10B981]'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${isSelected ? 'text-[#10B981]' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-[#0F172A]' : 'text-slate-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-[#0F172A] mb-4">Residential Status</h3>
                <div className="grid grid-cols-3 gap-3">
                  {RESIDENTIAL_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.residential_status === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setFormData(prev => ({ ...prev, residential_status: option.value }))}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-[#10B981] bg-emerald-50' 
                            : 'border-slate-200 hover:border-[#10B981]'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${isSelected ? 'text-[#10B981]' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-[#0F172A]' : 'text-slate-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-[#0F172A] mb-4">Monthly Income (Take-Home)</h3>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-center mb-4">
                    <span className="font-numbers text-3xl font-bold text-[#0F172A]">
                      {formatCurrency(formData.monthly_income)}
                    </span>
                  </div>
                  <Slider
                    value={[formData.monthly_income]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, monthly_income: value[0] }))}
                    min={0}
                    max={6000}
                    step={100}
                    className="w-full"
                  />
                  <div className="flex justify-between mt-2 text-xs text-slate-400">
                    <span>£0</span>
                    <span>£6,000+</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Creditors */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-2">About your creditors</h2>
                <p className="text-slate-600">Tell us about who you owe money to</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#0F172A] mb-4">Number of Creditors</h3>
                <div className="flex items-center justify-center gap-6 bg-slate-50 rounded-xl p-6">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, number_of_creditors: Math.max(1, prev.number_of_creditors - 1) }))}
                    className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-bold text-2xl hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                  >
                    -
                  </button>
                  <span className="font-numbers text-5xl font-bold text-[#0F172A] w-20 text-center">
                    {formData.number_of_creditors}
                  </span>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, number_of_creditors: Math.min(20, prev.number_of_creditors + 1) }))}
                    className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-bold text-2xl hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                  >
                    +
                  </button>
                </div>
                {formData.number_of_creditors < 2 && (
                  <p className="text-center text-sm text-amber-600 mt-2">
                    IVA requires at least 2 creditors
                  </p>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-[#0F172A] mb-4">Types of Debt (Select all that apply)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {DEBT_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.debt_types.includes(type.value);
                    return (
                      <button
                        key={type.value}
                        onClick={() => toggleDebtType(type.value)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-[#10B981] bg-emerald-50' 
                            : 'border-slate-200 hover:border-[#10B981]'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-[#10B981]' : 'text-slate-400'}`} />
                        <span className={`text-xs font-medium text-center ${isSelected ? 'text-[#0F172A]' : 'text-slate-600'}`}>
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8">
          {currentStep > 1 ? (
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <div></div>
          )}

          {currentStep < 3 ? (
            <Button onClick={handleNext} className="bg-[#10B981] hover:bg-[#059669]">
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#10B981] hover:bg-[#059669]"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Submitting...
                </span>
              ) : (
                <>
                  Submit Application
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StartCasePage;
